/**
 * EolMonitor.js — End-of-Life Monitoring
 * Shows EOL status for OS and applications across all endpoints.
 * Alerts 90 days before EOL. Tab inside Asset Discovery module.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_STYLE = {
  eol:         { bg: '#fee2e2', color: '#dc2626', icon: '🔴', label: 'End of Life' },
  eol_warning: { bg: '#fef3c7', color: '#d97706', icon: '🟡', label: 'EOL < 90 days' },
  supported:   { bg: '#dcfce7', color: '#16a34a', icon: '🟢', label: 'Supported' },
  unknown:     { bg: '#f1f5f9', color: '#94a3b8', icon: '⚪', label: 'Unknown' },
};

export default function EolMonitor() {
  const [alerts,   setAlerts]   = useState([]);
  const [checkedAt, setChecked] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [running,  setRunning]  = useState(false);
  const [filter,   setFilter]   = useState('all');  // all | eol | eol_warning
  const [msg,      setMsg]      = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      const r = await api.getEolAlerts();
      setAlerts(r.data.alerts || []);
      setChecked(r.data.checkedAt);
    } catch (e) {
      setMsg('Failed to load EOL data: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function runCheck() {
    setRunning(true);
    setMsg('');
    try {
      const r = await api.runEolCheck();
      setAlerts(r.data.alerts || []);
      setChecked(r.data.checkedAt);
      const eolC = (r.data.alerts||[]).filter(a => a.status === 'eol').length;
      const warnC = (r.data.alerts||[]).filter(a => a.status === 'eol_warning').length;
      const suppC = (r.data.alerts||[]).filter(a => a.status === 'supported').length;
      setMsg(`✔ EOL check complete — ${eolC} EOL, ${warnC} warnings, ${suppC} supported`);
      setTimeout(() => setMsg(''), 6000);
    } catch (e) {
      setMsg('EOL check failed: ' + (e.response?.data?.error || e.message));
    } finally { setRunning(false); }
  }

  const eolCount  = alerts.filter(a => a.status === 'eol').length;
  const warnCount = alerts.filter(a => a.status === 'eol_warning').length;
  const supported = alerts.filter(a => a.status === 'supported').length;

  const filtered = alerts.filter(a => {
    if (filter === 'all') return true;
    if (filter === 'issues') return a.status === 'eol' || a.status === 'eol_warning';
    return a.status === filter;
  });

  // Group by device
  const byDevice = filtered.reduce((acc, a) => {
    if (!acc[a.hostname]) acc[a.hostname] = [];
    acc[a.hostname].push(a);
    return acc;
  }, {});

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">📅 End-of-Life Monitor</div>
          <div className="page-subtitle">
            OS and application lifecycle tracking · 90-day advance alerts
            {checkedAt && <span style={{ color: '#94a3b8', marginLeft: 12 }}>
              Last checked: {fmtDate(checkedAt)}
            </span>}
          </div>
        </div>
        <button className="btn btn-primary" onClick={runCheck} disabled={running}>
          {running ? '⟳ Checking...' : '🔍 Run EOL Check'}
        </button>
      </div>

      {msg && (
        <div className={`alert ${msg.startsWith('✔') ? 'success' : 'error'}`}
          style={{ marginBottom: 16 }}>{msg}</div>
      )}

      {/* Summary cards */}
      <div className="cards-row" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 20 }}>
        <div className="card red"
          style={{ cursor:'pointer', outline: filter==='eol'?'2px solid #dc2626':undefined }}
          onClick={() => setFilter(filter==='eol'?'all':'eol')}>
          <div className="card-label">End of Life</div>
          <div className="card-value">{eolCount}</div>
          <div className="card-sub">{filter==='eol' ? '▼ filtered' : 'click to filter'}</div>
        </div>
        <div className="card amber"
          style={{ cursor:'pointer', outline: filter==='eol_warning'?'2px solid #d97706':undefined }}
          onClick={() => setFilter(filter==='eol_warning'?'all':'eol_warning')}>
          <div className="card-label">EOL Warning</div>
          <div className="card-value">{warnCount}</div>
          <div className="card-sub">{filter==='eol_warning' ? '▼ filtered' : 'within 90 days'}</div>
        </div>
        <div className="card green"
          style={{ cursor:'pointer', outline: filter==='supported'?'2px solid #16a34a':undefined }}
          onClick={() => setFilter(filter==='supported'?'all':'supported')}>
          <div className="card-label">Supported</div>
          <div className="card-value">{supported}</div>
          <div className="card-sub">{filter==='supported' ? '▼ filtered' : 'active support'}</div>
        </div>
        <div className="card blue"
          style={{ cursor:'pointer' }}
          onClick={() => setFilter('all')}>
          <div className="card-label">Devices Scanned</div>
          <div className="card-value">{Object.keys(alerts.reduce((a,x)=>{a[x.hostname]=1;return a;},{})).length}</div>
          <div className="card-sub">{filter==='all' ? '▼ showing all' : 'click to show all'}</div>
        </div>
      </div>

      {/* Info banner */}
      <div className="alert info" style={{ marginBottom: 20 }}>
        📅 <strong>EOL alerts are generated 90 days before end-of-life.</strong> Red = already EOL (no security patches from vendor).
        Yellow = within 90 days. Plan upgrades before EOL date to avoid security exposure.
      </div>

      {/* Filter buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { id: 'all',         label: `All (${alerts.length})` },
          { id: 'issues',      label: `⚠ Issues (${eolCount + warnCount})` },
          { id: 'eol',         label: `🔴 EOL (${eolCount})` },
          { id: 'eol_warning', label: `🟡 Warning (${warnCount})` },
          { id: 'supported',   label: `🟢 Supported (${supported})` },
        ].map(f => (
          <button key={f.id}
            className={`btn btn-sm ${filter === f.id ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
      </div>

      {/* EOL table */}
      {loading ? <div className="loading">Loading EOL data...</div> : (
        filtered.length === 0 ? (
          <div className="panel" style={{ padding: 48, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1e2a3b' }}>
              {alerts.length === 0
                ? 'No EOL data yet — click "Run EOL Check" to scan all endpoints'
                : 'No items match the selected filter'}
            </div>
          </div>
        ) : (
          Object.entries(byDevice).map(([hostname, items]) => (
            <div key={hostname} className="panel" style={{ marginBottom: 16 }}>
              <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f4ff',
                display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>🖥️</span>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1e2a3b' }}>{hostname}</div>
                <div style={{ display: 'flex', gap: 6, marginLeft: 8 }}>
                  {items.filter(i => i.status === 'eol').length > 0 && (
                    <span style={{ fontSize: 10, fontWeight: 700, background: '#fee2e2',
                      color: '#dc2626', padding: '2px 8px', borderRadius: 12 }}>
                      {items.filter(i => i.status === 'eol').length} EOL
                    </span>
                  )}
                  {items.filter(i => i.status === 'eol_warning').length > 0 && (
                    <span style={{ fontSize: 10, fontWeight: 700, background: '#fef3c7',
                      color: '#d97706', padding: '2px 8px', borderRadius: 12 }}>
                      {items.filter(i => i.status === 'eol_warning').length} warning
                    </span>
                  )}
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Product</th>
                    <th>Version</th>
                    <th>EOL Date</th>
                    <th>Days Remaining</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((a, i) => {
                    const ss = STATUS_STYLE[a.status] || STATUS_STYLE.unknown;
                    return (
                      <tr key={i} style={{ background: a.status === 'eol' ? '#fff8f8' : undefined }}>
                        <td>
                          <span style={{ fontSize: 11, fontWeight: 600, background: '#f1f5f9',
                            color: '#64748b', padding: '2px 8px', borderRadius: 10 }}>
                            {a.type === 'os' ? '🪟 OS' : '📦 App'}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{a.product}</div>
                        </td>
                        <td style={{ fontSize: 12, color: '#64748b' }}>
                          {a.version || '—'}
                        </td>
                        <td>
                          <div style={{ fontSize: 13, fontWeight: 600,
                            color: a.status === 'eol' ? '#dc2626' : a.status === 'eol_warning' ? '#d97706' : '#1e2a3b' }}>
                            {fmtDate(a.eolDate)}
                          </div>
                        </td>
                        <td>
                          {a.daysUntilEol !== null ? (
                            <div style={{ fontSize: 13, fontWeight: 700,
                              color: a.daysUntilEol < 0 ? '#dc2626' : a.daysUntilEol < 30 ? '#ea580c' : '#d97706' }}>
                              {a.daysUntilEol < 0
                                ? `${Math.abs(a.daysUntilEol)} days ago`
                                : `${a.daysUntilEol} days`}
                            </div>
                          ) : '—'}
                        </td>
                        <td>
                          <span style={{ fontSize: 11, fontWeight: 700, background: ss.bg,
                            color: ss.color, padding: '3px 10px', borderRadius: 12 }}>
                            {ss.icon} {ss.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))
        )
      )}
    </div>
  );
}
